import * as UI from "@iyk/ui"
import * as React from "react"

import { Dialog } from "@base-ui/react/dialog"
import { type Data, Match } from "effect"
import { ImageCdn } from "../files/ImageCdn.ts"
import { preventContextMenu } from "../hooks/usePreventContextMenu.ts"
import { LoadingImage } from "./LoadingImage.tsx"
import { MediaCarousel } from "./MediaCarousel.tsx"
import { VideoPlayer } from "./VideoPlayer.tsx"

const Root = ({ slides, children, onOpenChange }: MediaDialog.Root.Props) => {
  const popupRef = React.useRef<HTMLDivElement>(null)
  const [state, setState] = React.useState({
    isOpen: false,
    defaultIndex: 0
  })

  const open = (index: number) => {
    setState({ isOpen: true, defaultIndex: index })
    onOpenChange?.(true)
  }

  const close = () => {
    setState((prev) => ({ isOpen: false, defaultIndex: prev.defaultIndex }))
    onOpenChange?.(false)
  }

  return (
    <Context.Provider value={{ isOpen: state.isOpen, defaultIndex: state.defaultIndex, open, close }}>
      <Dialog.Root
        open={state.isOpen}
        onOpenChange={(isOpen) => {
          if (isOpen) {
            open(state.defaultIndex)
          } else {
            close()
          }
        }}
      >
        {children}

        <Dialog.Portal>
          <Dialog.Popup
            ref={popupRef}
            className={UI.cx(
              "fixed z-dialog top-0 left-0 right-0 h-dvh w-full",
              "bg-[var(--app-background-dark)] text-white",
              "duration-200 outline-none",
              "data-[starting-style]:opacity-0",
              "data-[ending-style]:opacity-0",
              "pb-4 sm:pt-4",
              "flex flex-col"
            )}
          >
            <div className="shrink-0 h-12 flex sm:hidden justify-end items-center px-3">
              <UI.Button type="button" kind="ghost" size="sm" onClick={close}>
                <UI.Icon.Close />
              </UI.Button>
            </div>

            <MediaCarousel.Root
              options={{ startIndex: state.defaultIndex }}
              containerClassName="grow min-h-0 w-full"
              className="size-full"
              indicatorSize="0%"
            >
              <Carousel slides={slides} keyboardScope={popupRef} />
            </MediaCarousel.Root>

            <ActionButton onClick={close} className="top-4 right-4 hidden sm:flex">
              <UI.Icon.Close />
            </ActionButton>
          </Dialog.Popup>
        </Dialog.Portal>
      </Dialog.Root>
    </Context.Provider>
  )
}

type CarouselProps = Pick<MediaDialog.Root.Props, "slides"> & {
  keyboardScope: React.RefObject<HTMLDivElement | null>
}

const Carousel = ({ slides, keyboardScope }: CarouselProps) => {
  const carousel = MediaCarousel.useCarousel()
  const { defaultIndex } = useInternalContext()
  const isInitialized = carousel.scrollSnapList().length > 0
  const activeSlide = isInitialized ? carousel.selectedScrollSnap() : defaultIndex
  const totalSlides = slides.length

  MediaCarousel.useArrowKeyNavigation(keyboardScope)

  return (
    <>
      <MediaCarousel.Slides
        slides={slides}
        classNames={{ viewport: "size-full [overflow:initial!important]" }}
      >
        {(slide, index) => (
          <MediaCarousel.Slide>
            <div
              className="relative size-full flex items-center justify-center px-4 sm:px-[76px]"
              onContextMenu={preventContextMenu}
            >
              {Match.valueTags(slide, {
                Image: (m) => (
                  <LoadingImage
                    className={classForMedia}
                    src={ImageCdn.src(m.fileUrl, { width: fullScreenImageWidth })}
                    loading={Math.abs(index - activeSlide) <= eagerWindow ? "eager" : "lazy"}
                    decoding="async"
                    spinnerClassName="size-8"
                  />
                ),
                Video: (m) => {
                  return (
                    <VideoPlayer
                      // TODO: ENG-5946: Use actual video id
                      id={`media-dialog-${m.playbackUrl}`}
                      kind="full"
                      playsInline
                      size="fill"
                      fit="contain"
                      src={m.playbackUrl}
                      poster={m.thumbnailUrl}
                      className={classForMedia}
                    />
                  )
                }
              })}
            </div>
          </MediaCarousel.Slide>
        )}
      </MediaCarousel.Slides>

      <ActionButton
        hidden={!carousel.canGoToPreviousSlide()}
        onClick={carousel.goToPreviousSlide}
        className="hidden sm:flex absolute top-1/2 -translate-y-1/2 left-4 z-50"
      >
        <UI.Icon.ChevronLeft className="size-5" />
      </ActionButton>
      <ActionButton
        hidden={!carousel.canGoToNextSlide()}
        onClick={carousel.goToNextSlide}
        className="hidden sm:flex absolute top-1/2 -translate-y-1/2 right-4 z-50"
      >
        <UI.Icon.ChevronRight className="size-5" />
      </ActionButton>

      <div className="absolute top-4 left-1/2 -translate-x-1/2 z-50 backdrop-blur-md rounded-md px-1 py-0.5 text-xs bg-black/30 tabular-nums">
        <span className="drop-shadow-[0_0_3px_rgba(0,0,0,6)]">
          {formatActiveSlide(activeSlide, totalSlides)}
        </span>
      </div>
    </>
  )
}

const formatActiveSlide = (activeSlide: number, totalSlides: number) => {
  const current = activeSlide + 1
  const displayCurrent = totalSlides > 9 && current < 10 ? `0${current}` : `${current}`

  return `${displayCurrent}/${totalSlides}`
}

const Trigger = ({ index, className, onClick, ...props }: MediaDialog.Trigger.Props) => {
  const { open } = useInternalContext()

  return (
    <button
      className={UI.cx("outline-none inline-flex focus-visible:ring-2 focus-visible:ring-gray-9", className)}
      onClick={(e) => {
        open(index)
        onClick?.(e)
      }}
      type="button"
      {...props}
    />
  )
}

const ActionButton = ({ className, hidden, children, ...props }: React.HTMLAttributes<HTMLButtonElement>) => {
  return (
    <button className={classForActionButton({ className, hidden })} {...props}>
      {children}
    </button>
  )
}

const fullScreenImageWidth = 2048
const eagerWindow = 2

const classForMedia = UI.cx("max-h-full max-w-full object-contain")
const classForActionButton = UI.cva(
  [
    "absolute z-50 cursor-pointer",
    "backdrop-blur-md bg-black/40 hover:bg-black/60 focus-visible:bg-black/60",
    "rounded-full size-11 flex items-center justify-center",
    "transition-opacity duration-300"
  ],
  {
    variants: {
      hidden: {
        true: "opacity-0",
        false: "opacity-100"
      }
    }
  }
)

type Media = Data.TaggedEnum<{
  Image: {
    fileUrl: string
  }
  Video: {
    thumbnailUrl: string
    playbackUrl: string
  }
}>

type Context = {
  isOpen: boolean
  defaultIndex: number
  open: (index: number) => void
  close: () => void
}
const Context = React.createContext<Context>({
  isOpen: false,
  defaultIndex: 0,
  open: () => {},
  close: () => {}
})

const useInternalContext = () => React.use(Context)

export const MediaDialog = {
  Root,
  Trigger
}

export namespace MediaDialog {
  export namespace Root {
    export type Props = {
      children: React.ReactNode
      onOpenChange?: (open: boolean) => void
      slides: Array<Media>
    }
  }

  export namespace Trigger {
    export type Props = React.HTMLAttributes<HTMLButtonElement> & {
      index: number
    }
  }
}
